import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { 
    useGetAppUserDetails, 
    useAssignUserToOrganization, 
    useRemoveUserFromOrganization,
    useUpdateAppUserPassword
} from '../services/appService';

function UserEditModal({ open, onClose, onUserUpdated, user, appId }) {
    const [userDetails, setUserDetails] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selectedOrgToAdd, setSelectedOrgToAdd] = useState('');
    const [isAssigning, setIsAssigning] = useState(false);
    const [isRemoving, setIsRemoving] = useState(false);
    const [currentMemberships, setCurrentMemberships] = useState([]);
    const [availableOrgs, setAvailableOrgs] = useState([]);

    // Password Reset State
    const [newPassword, setNewPassword] = useState('');
    const [passwordLoading, setPasswordLoading] = useState(false);
    const [passwordMessage, setPasswordMessage] = useState({ type: '', text: '' });

    const getUserDetailsHook = useGetAppUserDetails();
    const assignUserHook = useAssignUserToOrganization();
    const removeUserHook = useRemoveUserFromOrganization();
    const updatePasswordHook = useUpdateAppUserPassword();

    const fetchUserDetails = useCallback(async () => {
        if (open && user && appId) {
            setLoading(true);
            setError('');
            setUserDetails(null);
            setCurrentMemberships([]);
            setAvailableOrgs([]);
            // Reset password state on open
            setNewPassword('');
            setPasswordMessage({ type: '', text: '' });
            
            try {
                const data = await getUserDetailsHook(appId, user.id);
                setUserDetails(data);
                setCurrentMemberships(data.organizations || []);
                // Filter available orgs: exclude those the user is already in
                const memberOrgIds = (data.organizations || []).map(org => org.id);
                setAvailableOrgs((data.allOrganizations || []).filter(org => !memberOrgIds.includes(org.id)));
            } catch (err) {
                console.error("Error fetching user details:", err);
                setError(err.message || 'Failed to load user details.');
            } finally {
                setLoading(false);
            }
        }
    }, [open, user, appId, getUserDetailsHook]);

    useEffect(() => {
        fetchUserDetails();
    }, [fetchUserDetails]); 

    const handleAssignOrg = async () => {
        if (!selectedOrgToAdd || !user || !appId) return;
        setIsAssigning(true);
        setError('');
        try {
            await assignUserHook(appId, user.id, selectedOrgToAdd);
            setSelectedOrgToAdd(''); // Clear selection
            await fetchUserDetails(); // Refresh details after assignment
             onUserUpdated(); // Notify parent page
        } catch (err) {
            console.error("Error assigning user to organization:", err);
            setError(err.message || 'Failed to assign organization.');
        } finally {
            setIsAssigning(false);
        }
    };

    const handleRemoveOrg = async (organizationId) => {
        if (!organizationId || !user || !appId) return;
        setIsRemoving(true); 
        setError('');
        try {
            await removeUserHook(appId, user.id, organizationId);
            await fetchUserDetails(); // Refresh details after removal
            onUserUpdated(); // Notify parent page
        } catch (err) {
            console.error("Error removing user from organization:", err);
            setError(err.message || 'Failed to remove organization.');
        } finally {
            setIsRemoving(false);
        }
    };

    const handleUpdatePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            setPasswordMessage({ type: 'error', text: 'Password must be at least 6 characters.' });
            return;
        }
        setPasswordLoading(true);
        setPasswordMessage({ type: '', text: '' });
        try {
            await updatePasswordHook(appId, user.id, newPassword);
            setPasswordMessage({ type: 'success', text: 'Password updated successfully.' });
            setNewPassword(''); // Clear input
        } catch (err) {
            console.error("Error updating password:", err);
            setPasswordMessage({ type: 'error', text: err.message || 'Failed to update password.' });
        } finally {
            setPasswordLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Edit User / Manage Organizations</DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress /></Box>
                )}
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {!loading && userDetails && (
                    <Box>
                        <Typography variant="h6" gutterBottom>User Details</Typography>
                        <TextField
                            label="User ID"
                            fullWidth
                            value={userDetails.id}
                            disabled
                            margin="normal"
                        />
                         <TextField
                            label="Display Name"
                            fullWidth
                            value={userDetails.display_name || 'N/A'}
                            disabled 
                            margin="normal"
                        />
                         <TextField
                            label="Email"
                            fullWidth
                            value={user?.email || 'N/A'} 
                            disabled
                            margin="normal"
                        />

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Current Memberships</Typography>
                        {currentMemberships.length > 0 ? (
                            <List dense>
                                {currentMemberships.map((org) => (
                                    <ListItem
                                        key={org.id}
                                        secondaryAction={
                                            <IconButton
                                                edge="end"
                                                aria-label="remove"
                                                onClick={() => handleRemoveOrg(org.id)}
                                                disabled={isRemoving}
                                                color="error"
                                            >
                                                {isRemoving ? <CircularProgress size={20} /> : <RemoveCircleOutlineIcon />}
                                            </IconButton>
                                        }
                                    >
                                        <ListItemText primary={org.name} secondary={`ID: ${org.id}`} />
                                    </ListItem>
                                ))}
                            </List>
                        ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                Not a member of any organizations.
                            </Typography>
                        )}

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Assign to Organization</Typography>
                        {availableOrgs.length > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="assign-org-label">Select Organization</InputLabel>
                                    <Select
                                        labelId="assign-org-label"
                                        value={selectedOrgToAdd}
                                        label="Select Organization"
                                        onChange={(e) => setSelectedOrgToAdd(e.target.value)}
                                        disabled={isAssigning}
                                    >
                                        <MenuItem value=""><em>None</em></MenuItem>
                                        {availableOrgs.map((org) => (
                                            <MenuItem key={org.id} value={org.id}>{org.name} ({org.id})</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                <IconButton
                                    color="primary"
                                    onClick={handleAssignOrg}
                                    disabled={!selectedOrgToAdd || isAssigning}
                                    aria-label="assign organization"
                                >
                                    {isAssigning ? <CircularProgress size={24} /> : <AddCircleOutlineIcon />}
                                </IconButton>
                            </Box>
                        ) : (
                             <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                                No available organizations to assign (already member of all?).
                            </Typography>
                        )}

                        <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Change Password</Typography>
                        {passwordMessage.text && (
                            <Alert severity={passwordMessage.type} sx={{ mb: 2 }}>{passwordMessage.text}</Alert>
                        )}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                             <TextField
                                label="New Password"
                                type="password"
                                fullWidth
                                size="small"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={passwordLoading}
                                placeholder="Minimum 6 characters"
                            />
                            <Button 
                                variant="contained" 
                                onClick={handleUpdatePassword}
                                disabled={!newPassword || passwordLoading}
                            >
                                {passwordLoading ? <CircularProgress size={24} /> : 'Update'}
                            </Button>
                        </Box>

                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

UserEditModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUserUpdated: PropTypes.func.isRequired,
    user: PropTypes.object, 
    appId: PropTypes.string.isRequired,
};

export default UserEditModal;